/** Audited teaching sequence. See docs/DATA_FLOW_AUDIT.md for legacy corrections.
 * The campus is a functional illustration, not a surveyed mission installation. */
export const FLOW_SOURCES = {
  jason: {
    label: 'Jason-3 product handbook · §§2.4–2.5',
    url: 'https://user.eumetsat.int/s3/eup-strapi-media/pdf_j3_prod_handbook_3cd70eba1b.pdf',
  },
  cnes: {
    label: 'CNES · Jason spacecraft and S-band telemetry',
    url: 'https://cnes.fr/projets/jason/satellites',
  },
  dcs: {
    label: 'NOAA · GOES Data Collection System',
    url: 'https://dcs2.noaa.gov/program_information/index.html',
  },
  tdrs: {
    label: 'NASA · space relay compatibility',
    url: 'https://www.nasa.gov/smallsat-institute/sst-soa/ground-data-systems-and-mission-operations/',
  },
  levels: {
    label: 'NASA · EOSDIS processing-level definitions',
    url: 'https://www.earthdata.nasa.gov/s3fs-public/2024-02/ESDS-RFC-050-EOSDIS-Processing%20Levels-v1.pdf',
  },
  coding: {
    label: 'CCSDS 131.0 · synchronization and channel coding',
    url: 'https://ccsds.org/view/bluebooks/entry/4803/',
  },
  er2: {
    label: 'NASA · ER-2 airborne science',
    url: 'https://www.nasa.gov/centers-and-facilities/armstrong/er-2-aircraft/',
  },
  oceanus: { label: 'WHOI · Oceanus research vessel archive', url: 'https://archives.whoi.edu/oceanus/' },
  archive: {
    label: 'NOAA NCEI · Jason-3 telemetry archive',
    url: 'https://www.ncei.noaa.gov/access/metadata/landing-page/bin/iso?id=gov.noaa.nodc%3AJason3-Telemetry',
  },
} as const;

export interface FlowAsset {
  id: string;
  label: string;
  role: string;
  subview?: string;
}
export interface FlowStep {
  id: number;
  name: string;
  short: string;
  title: string;
  detail: string;
  input: string;
  action: string;
  output: string;
  assets: FlowAsset[];
  process: string[];
  components: string[];
  sources: (keyof typeof FLOW_SOURCES)[];
  note?: string;
}
const rf = { id: 'rf_antenna', label: 'RF antenna', role: 'Dish, feed and receiving equipment' };
const rx = {
  id: 'rf_antenna',
  label: 'Receive equipment',
  role: 'Inside the container · RX rack',
  subview: 'container',
};
const ground = { id: 'ground_station', label: 'Ground station', role: 'Inspect the processing cabinets' };
const centre = { id: 'data_center', label: 'Data centre', role: 'Compute, storage and archive' };
const cloud = { id: 'data_cloud', label: 'Cloud services', role: 'Product access and distribution' };

export const FLOW_STEPS: readonly FlowStep[] = [
  {
    id: 1,
    name: 'Data acquisition',
    short: 'Observe',
    title: 'Measure the environment',
    detail: 'Space, air, ocean and field instruments collect complementary observations.',
    input: 'Earth and atmosphere',
    action: 'Measure & time-tag',
    output: 'Instrument measurements',
    assets: [
      { id: 'senseSat_01', label: 'Jason-3', role: 'Radar altimetry · ocean surface' },
      { id: 'er_2', label: 'ER-2 aircraft', role: 'Airborne remote sensing and sampling' },
      { id: 'boat', label: 'R/V Oceanus', role: 'Research vessel · ocean measurements' },
      { id: 'terrestrial', label: 'In-situ station', role: 'Local meteorological measurements' },
      { id: 'soldier', label: 'Field observer', role: 'Illustrative ground observations' },
    ],
    process: [
      'Jason-3 records radar echoes; its microwave radiometer and orbit measurements support subsequent altimetry corrections. Measurement pulses directed at the sea are distinct from the telemetry downlink.',
      'ER-2 instrument payloads support remote sensing, atmospheric sampling and satellite validation. Vessel, station and field observations provide complementary measurements; their communications depend on the deployed equipment.',
    ],
    components: ['altimeter', 'amr', 'gps_receiver'],
    sources: ['jason', 'er2', 'oceanus'],
  },
  {
    id: 2,
    name: 'Direct downlink',
    short: 'Downlink',
    title: 'Downlink during ground contact',
    detail: 'Jason-3 sends telemetry directly to a ground station when it is in view.',
    input: 'Onboard telemetry',
    action: 'Transmit over S-band',
    output: 'RF at the ground antenna',
    assets: [{ id: 'senseSat_01', label: 'Jason-3', role: 'Direct-to-ground telemetry' }, rf],
    process: [
      'Jason-3 uses S-band telemetry. The product handbook identifies direct reception at Wallops, Fairbanks and Usingen; acquisition depends on station visibility and the scheduled contact.',
      'A signal follows the station-to-spacecraft slant range, not a fixed vertical altitude. Propagation delay, contact duration and data rate are distinct quantities.',
    ],
    components: ['primary_reflector', 'feed_horn'],
    sources: ['cnes', 'jason'],
  },
  {
    id: 3,
    name: 'Satellite relay',
    short: 'Relay',
    title: 'Relay remote sensor reports',
    detail: 'A GOES Data Collection System example: remote platform → GOES → receiving station.',
    input: 'Remote platform message',
    action: 'Relay via GOES DCS',
    output: 'Relayed platform message',
    assets: [
      { id: 'terrestrial', label: 'In-situ station', role: 'Illustrative DCS-equipped platform' },
      { id: 'goes', label: 'GOES', role: 'Data Collection System relay' },
      rf,
      { id: 'tdrs', label: 'TDRS', role: 'Separate relay system · compatible users' },
    ],
    note: 'Alternative to direct downlink, not an additional hop in Jason-3’s path.',
    process: [
      'GOES DCS relays environmental messages from equipped remote platforms to ground receiving and processing systems. The field station illustrates such a platform; its DCS transmitter is not independently identified in this model.',
      'TDRS is a different relay network for compatible user spacecraft. This demonstration does not assert a Jason-3-to-TDRS or Jason-3-to-GOES link.',
      'RF and optical terminals require compatible links. The optical antenna remains available for inspection; no laser link is invented for these collectors.',
    ],
    components: [],
    sources: ['dcs', 'tdrs'],
  },
  {
    id: 4,
    name: 'Reception & conditioning',
    short: 'Receive',
    title: 'Capture and condition the signal',
    detail: 'The RF front end and receive rack prepare the signal for the modem.',
    input: 'Received RF',
    action: 'Amplify · translate · select IF',
    output: 'Conditioned IF to modem',
    assets: [
      rf,
      rx,
      {
        id: 'optical_antenna',
        label: 'Optical terminal',
        role: 'Separate technology · compatible optical links only',
      },
    ],
    process: [
      'The reflector and feed collect RF energy. Low-noise amplification preserves sensitivity; filtering, gain control and frequency conversion prepare the receiver input.',
      'The RX frequency translator and IF switching select the signal path to the modem. The spectrum analyzer monitors a tap; it is not a serial payload-processing stage.',
      'IF remains a modulated signal. A digital bitstream is recovered by the receiver in the next step. Gain, IF frequency and receive level depend on the installed chain.',
    ],
    components: [
      'primary_reflector',
      'feed_horn',
      'low_noise_amplifier',
      'freq_translator',
      'switch_matrix',
      'spectrum_analyzer',
    ],
    sources: ['tdrs', 'coding'],
  },
  {
    id: 5,
    name: 'Demodulation & decoding',
    short: 'Decode',
    title: 'Recover the transmitted data',
    detail: 'Receiver synchronization and channel decoding recover frames, with quality flags retained.',
    input: 'Conditioned IF',
    action: 'Recover symbols · decode · validate',
    output: 'Validated telemetry frames',
    assets: [rx, { ...ground, role: 'Signal-processing intake · first cabinet' }],
    process: [
      'Carrier and symbol-timing recovery support demodulation. Synchronization, de-randomization and forward-error correction follow the mission’s configured coding profile; their detailed ordering depends on that profile.',
      'Frame checks and sequence tracking identify corrupt or missing data. Decoding does not guarantee an error-free stream and has no universal correctable bit-error percentage.',
      'In the RF model, the satellite modem and mission-link modem are in the RX rack. Recovered digital telemetry passes through its network output to the facility’s signal-processing intake.',
    ],
    components: ['modem', 'mission_modem', 'network_switch', 'data_processing_signal_processing'],
    sources: ['coding'],
  },
  {
    id: 6,
    name: 'Reconstruction & formatting',
    short: 'Format',
    title: 'Reconstruct the record',
    detail: 'Recover source packets, retain timing and quality, and assemble the Level 0 record.',
    input: 'Telemetry frames',
    action: 'Extract · sequence · preserve metadata',
    output: 'Reconstructed Level 0 data',
    assets: [{ ...ground, role: 'Data formatting · second cabinet' }],
    process: [
      'Extract and reassemble source packets or mission-defined records from the recovered frames. Science and housekeeping streams are identified and routed without losing their timing or quality context.',
      'Level 0 retains the unprocessed instrument data at full resolution, with communications packaging and duplicates removed. Record gaps and processing provenance.',
      'CCSDS source packets are commonly formed onboard and recovered on the ground. Ground IP transport and science-product file formats are separate layers; packetization alone is not scientific calibration.',
    ],
    components: [
      'data_processing_formatting',
      'extract_payload_data',
      'format_into_packets',
      'add_metadata_headers',
    ],
    sources: ['levels', 'coding'],
  },
  {
    id: 7,
    name: 'Calibration & science products',
    short: 'Calibrate',
    title: 'Derive science products',
    detail: 'Apply mission algorithms, orbit information and quality control to derive geophysical products.',
    input: 'L0 data + ancillary information',
    action: 'Calibrate · locate · derive',
    output: 'Quality-controlled science products',
    assets: [
      { ...ground, role: 'Data calibration · third cabinet' },
      { id: 'senseSat_01', label: 'Jason-3 instruments', role: 'Altimetry and supporting measurements' },
    ],
    process: [
      'NASA EOSDIS: L1A adds timing and ancillary information to unprocessed data; L1B applies conversion to sensor units. L2 contains derived geophysical variables; L3 maps variables onto space/time grids; L4 contains model or analysis results. These are product categories, not a compulsory linear chain.',
      'Jason-3 processing combines altimeter range and orbit altitude with propagation and geophysical corrections. Mission products include sea-surface height, significant wave height and wind estimates, with associated quality information.',
      'Use the mission product handbook to interpret product levels and variables. L1A is not generically calibrated; Landsat L1TP is not a universal altimetry level; L3 is not necessarily gap-free.',
    ],
    components: [
      'data_processing_calibration',
      'radiometric_calibration',
      'geometric_correction',
      'atmospheric_correction',
      'gridding_temporal_compositing',
      'model_integration_analysis',
    ],
    sources: ['levels', 'jason'],
  },
  {
    id: 8,
    name: 'Product routing',
    short: 'Route',
    title: 'Route data products',
    detail: 'Route identified, versioned products to operational services and archive systems.',
    input: 'Data products + metadata',
    action: 'Route · transfer · verify delivery',
    output: 'Products at subscribed destinations',
    assets: [{ ...ground, role: 'Data distribution · outgoing network' }, centre, cloud],
    process: [
      'Distribution preserves product identity, version, timestamps and quality information. Operational delivery and archive ingestion can proceed in parallel.',
      'Jason-3 operational, interim and final records have different processing and orbit inputs. Latency is product-specific; this campus animation does not measure transfer rates or delivery time.',
    ],
    components: [
      'data_processing_distribution',
      'direct_distribution',
      'data_center_distribution',
      'data_cloud_distribution',
    ],
    sources: ['jason', 'archive'],
  },
  {
    id: 9,
    name: 'Archive & publication',
    short: 'Archive',
    title: 'Preserve and publish the record',
    detail: 'Keep data, metadata and processing versions discoverable and available for reuse.',
    input: 'Delivered data and metadata',
    action: 'Ingest · catalogue · preserve',
    output: 'Discoverable, retrievable datasets',
    assets: [centre, cloud],
    process: [
      'Archive ingestion checks file integrity and records metadata, provenance and access information. Preserve raw and derived records according to the mission’s data policy, so products can be reprocessed and compared.',
      'NOAA NCEI archives Jason-3 telemetry. The illustrated data hall and cloud service represent storage and access functions, not a measured inventory or retention commitment.',
    ],
    components: ['data_storage', 'archives', 'networking', 'servers', 'hvac'],
    sources: ['archive', 'jason'],
  },
  {
    id: 10,
    name: 'User access & applications',
    short: 'Use',
    title: 'Use the environmental information',
    detail: 'Analysts combine appropriate products with models and other observations for decisions.',
    input: 'Available environmental products',
    action: 'Access · assess · apply',
    output: 'Analysis and decision support',
    assets: [
      {
        id: 'weather_center',
        label: 'Weather analysis centre',
        role: 'Forecasting and environmental analysis',
      },
      { id: 'soldier', label: 'DOD field unit', role: 'Illustrative environmental decision support' },
    ],
    process: [
      'Weather services and researchers select products by coverage, timeliness, resolution and quality. Satellite measurements support ocean and climate analysis and can inform forecast systems; a measurement is not itself a forecast.',
      'The field unit illustrates another consumer of environmental information. No direct Jason-3 weapons-guidance feed or specific military network capability is claimed.',
    ],
    components: [],
    sources: ['jason'],
  },
];
export const FLOW_STEP_COUNT = FLOW_STEPS.length;
export const flowStep = (id: number) => FLOW_STEPS.find((step) => step.id === id)!;

/** Physical destinations are stable even where several steps share a building. */
export const processingPartFor = (step: number) =>
  step === 5
    ? 'data_processing_signal_processing'
    : step === 6
      ? 'data_processing_formatting'
      : step === 7
        ? 'data_processing_calibration'
        : step === 8
          ? 'data_processing_distribution'
          : null;
